import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { createId, init } from '@paralleldrive/cuid2'
import { IconPhotoCheck, IconX } from '@tabler/icons-react'
import { RcFile } from 'antd/lib/upload'
import * as markerjs2 from 'markerjs2'
import styled from 'styled-components'

import { Button } from '@/components/shared-components/BUI/Button'
import { Modal } from '@/components/shared-components/BUI/Modal'
import { DozerLoading } from '@/components/shared-components/DozerLoading'
import { FileUploadResult } from '@/hooks/useAttachmentUpload'

import { buildPassTheme } from 'src/styles/theme'

const TOOLBAR_HEIGHT = 40

const Content = styled.div`
  width: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  justify-items: center;
  align-items: center;
  gap: ${TOOLBAR_HEIGHT * 2}px;
`

const Footer = styled.div`
  background-color: ${({ theme }) => theme.colour.neutral.white};
  display: flex;
  justify-content: center;
  gap: 1rem;
`

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`

const ImageToMarkup = styled.img`
  max-width: 100%; // stay within the confines of parent
  max-height: 100%; // stay within the confines of parent
  min-width: 200px; // ensure tiny images are still editable
  min-height: 200px; // ensure tiny images are still editable
  object-fit: contain;
  object-position: center;
  visibility: hidden;
`

const getMarkupFileName = (fileName: string) => {
  const ext = fileName.split('.').pop()

  if (!ext) return `${fileName}-markup`

  const name = fileName.replace(`.${ext}`, '')
  return `${name}-markup.${ext}`
}

type Props = {
  file: FileUploadResult
  closeModal: () => void
  insertFile: (newFile: RcFile, oldFile: FileUploadResult) => Promise<void>
}

export const MarkupModal: React.FC<Props> = ({
  file,
  closeModal,
  insertFile,
}) => {
  const imageRef = useRef<HTMLImageElement>(undefined)
  const imageWrapperRef = useRef<HTMLDivElement>(undefined)
  const markerRef = useRef<markerjs2.MarkerArea>(undefined)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const cancelAndClose = useCallback(() => {
    markerRef.current?.close()
    closeModal()
  }, [closeModal])

  const imageUrl = useMemo(() => {
    const url = new URL(file.url)
    url.searchParams.set(
      'cache-bust',
      init({
        length: 6,
      })()
    )
    return url.toString()
  }, [file])

  useLayoutEffect(() => {
    const img = imageRef.current

    const saveAttachment = async (event: markerjs2.MarkerAreaRenderEvent) => {
      setSaving(true)

      const blob = await fetch(event.dataUrl).then((r) => r.blob()) // convert to blob
      const newFile = new File([blob], getMarkupFileName(file.name), {
        type: 'image/png',
        lastModified: Date.now(),
      }) as RcFile // turn blob into file

      newFile.uid = createId() // add uid to turn File into RcFile

      await insertFile(newFile, file) // call insertFile from AttachmentUpload.tsx

      setSaving(false)
      cancelAndClose()
    }

    img.onload = () => {
      setLoading(false)

      if (img !== null) {
        const markerArea = new markerjs2.MarkerArea(img)

        markerArea.uiStyleSettings.zIndex = '50000' // needs to be above Ant modal
        markerArea.uiStyleSettings.resultButtonBlockVisible = false // hide save and cancel buttons
        markerArea.uiStyleSettings.toolbarHeight = TOOLBAR_HEIGHT
        markerArea.renderAtNaturalSize = true
        markerArea.renderImageType = 'image/png'

        markerArea.targetRoot = imageWrapperRef.current
        markerArea.addEventListener('render', saveAttachment)
        markerArea.show()
        markerArea.createNewMarker(markerjs2.FreehandMarker) // default to drawing freehand
        markerRef.current = markerArea
      }
    }
  }, [cancelAndClose, file, insertFile])

  const save = () => {
    markerRef.current?.startRenderAndClose()
  }

  return (
    <Modal
      open
      fullscreen
      closable={false}
      bodyStyle={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: buildPassTheme.colour.secondary.base,
      }}
      footerLayout="center"
      footer={
        <Footer>
          <Button
            variant="primary"
            icon={<IconPhotoCheck size={16} />}
            onClick={save}
            loading={saving}
          >
            Save changes
          </Button>
          <Button
            variant="secondary"
            icon={<IconX size={16} />}
            onClick={cancelAndClose}
            disabled={saving}
          >
            Cancel
          </Button>
        </Footer>
      }
    >
      <DozerLoading loading={loading || saving} noBackground />

      <Content>
        <ImageWrapper ref={imageWrapperRef}>
          <ImageToMarkup
            src={imageUrl}
            alt={file.fileName}
            ref={imageRef}
            crossOrigin="anonymous"
          />
        </ImageWrapper>
      </Content>
    </Modal>
  )
}
